import { RotateCcw } from "lucide-react";
import * as React from "react";
import { toast } from "sonner";

import type { DagNodeWithState } from "@/api/dag";
import type { LifecycleStateLabel } from "@/api/projects";
import { useRetryRun } from "@/api/submission";
import { Button } from "@/components/ui/button";

/**
 * Flow-level "Retry all failed" control.
 *
 * Counts the overlay's failed nodes with a known run_id and fires
 * one retry POST per node when clicked. Surfaces an aggregate toast
 * ("Retried N of M failed nodes" or "All N retries failed"). On
 * mixed-outcome — some succeed, some 409 — both toasts fire so the
 * user sees the actual split.
 *
 * Client-side fan-out (rather than a server bulk endpoint) keeps the
 * retry semantics + auth gating identical to the per-node retry; at
 * pilot scale (~tens of failures) the round-trip count is fine. A
 * server-batch endpoint becomes worthwhile if a Flow ever fails >100
 * children — not the current regime.
 *
 * The component is hidden when no overlay-fed failed nodes exist, so
 * the structural-only DAG preview surface is unaffected.
 */
export interface BulkRetryControlProps {
  /** Overlay nodes — usually ``dag.data?.nodes``. Only nodes with a
   * non-null run_id + a ``failed_*`` status contribute to the count. */
  nodes: DagNodeWithState[] | undefined;
  className?: string;
}

const RETRYABLE_STATES: ReadonlySet<LifecycleStateLabel> = new Set([
  "failed_validation",
  "failed_system",
  "failed_contract",
  "failed_scientific",
]);

function retryableNodes(
  nodes: DagNodeWithState[] | undefined
): Array<DagNodeWithState & { run_id: string }> {
  if (!nodes) return [];
  const out: Array<DagNodeWithState & { run_id: string }> = [];
  for (const n of nodes) {
    if (n.run_id && n.status && RETRYABLE_STATES.has(n.status)) {
      out.push(n as DagNodeWithState & { run_id: string });
    }
  }
  return out;
}

export function BulkRetryControl({ nodes, className }: BulkRetryControlProps): JSX.Element | null {
  const retry = useRetryRun();
  const [inFlight, setInFlight] = React.useState(false);
  const failed = React.useMemo(() => retryableNodes(nodes), [nodes]);
  if (failed.length === 0) return null;

  const onClick = async (): Promise<void> => {
    setInFlight(true);
    try {
      // Fire all retries in parallel — the runs.py endpoint is
      // per-run, but TanStack's mutateAsync lets us bundle the
      // collection. Promise.allSettled so one 409 doesn't drop the
      // remaining retries.
      const results = await Promise.allSettled(
        failed.map((n) => retry.mutateAsync({ runId: n.run_id }))
      );
      const succeeded = results.filter((r) => r.status === "fulfilled").length;
      const failedCount = results.length - succeeded;
      if (succeeded > 0) {
        toast.success(
          succeeded === results.length
            ? `Retried ${succeeded} failed node${succeeded === 1 ? "" : "s"}`
            : `Retried ${succeeded} of ${results.length} failed nodes`
        );
      }
      if (failedCount > 0) {
        toast.error(
          failedCount === results.length
            ? `All ${failedCount} retries failed`
            : `${failedCount} of ${results.length} retries failed`
        );
      }
    } finally {
      setInFlight(false);
    }
  };

  return (
    <Button
      size="sm"
      variant="secondary"
      onClick={onClick}
      disabled={inFlight}
      aria-label={`retry all ${failed.length} failed nodes`}
      className={className}
    >
      <RotateCcw className="mr-1.5 h-3 w-3" aria-hidden />
      {inFlight ? "Retrying…" : `Retry all failed (${failed.length})`}
    </Button>
  );
}
